'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { ArrowRight, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  ErrorMessage,
  LabelInputContainer,
} from '@/components/GeneralContainers';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { themes, useTheme } from '@/contexts/ThemeContext';
import { MessageService } from '@/utils/message';

const contactFormSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(50, 'First name is too long'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(50, 'Last name is too long'),
  email: z.string().email('Invalid email format'),
  message: z
    .string()
    .min(10, 'Message must be longer')
    .max(1000, 'Message is too long'),
});

type ContactFormData = z.infer<typeof contactFormSchema>;

export default function ContactSection() {
  const { currentTheme } = useTheme();
  const [visible, setVisible] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
  });

  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('contact');
      if (element) {
        const rect = element.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom >= 0;
        setVisible(isVisible);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial visibility
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);

    try {
      await MessageService.sendMessage({
        name: `${data.firstName} ${data.lastName}`,
        email: data.email,
        message: data.message,
      });

      setIsDialogOpen(true);
      reset();
    } catch (error) {
      console.error('Error sending message:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div
        id="contact"
        className="relative w-full overflow-hidden py-24 sm:py-32"
        style={{ background: themes[currentTheme].bgGradient }}
      >
        {/* Background gradient circles */}
        <motion.div
          className="absolute right-0 top-0 size-[600px] rounded-full opacity-30 blur-[120px]"
          style={{ background: themes[currentTheme].gradient }}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.3 }}
          transition={{ duration: 1.5 }}
        />
        <motion.div
          className="absolute bottom-0 left-0 size-[500px] rounded-full opacity-20 blur-[100px]"
          style={{ background: themes[currentTheme].gradient }}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.2 }}
          transition={{ duration: 1.5, delay: 0.2 }}
        />

        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <motion.h2
              className="text-base font-semibold leading-7"
              style={{ color: themes[currentTheme].accent }}
              initial={{ opacity: 0, y: 20 }}
              animate={visible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              Contact Us
            </motion.h2>
            <motion.p
              className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={visible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              style={{ color: themes[currentTheme].text }}
            >
              Let&apos;s Build Something Great Together
            </motion.p>
            <motion.p
              className="mt-6 text-lg leading-8"
              initial={{ opacity: 0, y: 20 }}
              animate={visible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              style={{ color: themes[currentTheme].textMuted }}
            >
              Ready to take your startup to the next level? We&apos;re always
              looking for ambitious founders with groundbreaking ideas.
            </motion.p>
          </div>
          <motion.form
            className="mx-auto mt-16 max-w-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={visible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
              <LabelInputContainer>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-semibold leading-6"
                  style={{ color: themes[currentTheme].text }}
                >
                  First name
                  <input
                    type="text"
                    id="firstName"
                    autoComplete="given-name"
                    className="mt-2.5 block w-full rounded-md border-0 bg-white/5 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-[#98E2D5] sm:text-sm sm:leading-6"
                    style={{ color: themes[currentTheme].text }}
                    {...register('firstName')}
                  />
                </label>
                {errors.firstName && (
                  <ErrorMessage message={errors.firstName.message!} />
                )}
              </LabelInputContainer>

              <LabelInputContainer>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-semibold leading-6"
                  style={{ color: themes[currentTheme].text }}
                >
                  Last name
                  <input
                    type="text"
                    id="lastName"
                    autoComplete="family-name"
                    className="mt-2.5 block w-full rounded-md border-0 bg-white/5 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-[#98E2D5] sm:text-sm sm:leading-6"
                    style={{ color: themes[currentTheme].text }}
                    {...register('lastName')}
                  />
                </label>
                {errors.lastName && (
                  <ErrorMessage message={errors.lastName.message!} />
                )}
              </LabelInputContainer>

              <div className="sm:col-span-2">
                <LabelInputContainer>
                  <label
                    htmlFor="email"
                    className="block text-sm font-semibold leading-6"
                    style={{ color: themes[currentTheme].text }}
                  >
                    Email
                    <input
                      type="email"
                      id="email"
                      autoComplete="email"
                      className="mt-2.5 block w-full rounded-md border-0 bg-white/5 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-[#98E2D5] sm:text-sm sm:leading-6"
                      style={{ color: themes[currentTheme].text }}
                      {...register('email')}
                    />
                  </label>
                  {errors.email && (
                    <ErrorMessage message={errors.email.message!} />
                  )}
                </LabelInputContainer>
              </div>

              <div className="sm:col-span-2">
                <LabelInputContainer>
                  <label
                    htmlFor="message"
                    className="block text-sm font-semibold leading-6"
                    style={{ color: themes[currentTheme].text }}
                  >
                    Message
                    <textarea
                      id="message"
                      rows={4}
                      className="mt-2.5 block w-full rounded-md border-0 bg-white/5 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-inset focus:ring-[#98E2D5] sm:text-sm sm:leading-6"
                      style={{ color: themes[currentTheme].text }}
                      {...register('message')}
                    />
                  </label>
                  {errors.message && (
                    <ErrorMessage message={errors.message.message!} />
                  )}
                </LabelInputContainer>
              </div>
            </div>
            <div className="mt-10 flex justify-center sm:justify-end">
              <motion.button
                type="submit"
                disabled={isSubmitting}
                className="group relative inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                  background: themes[currentTheme].gradient,
                }}
                whileHover={{
                  scale: isSubmitting ? 1 : 1.05,
                  boxShadow: isSubmitting
                    ? 'none'
                    : `0 0 30px ${themes[currentTheme].primary}60`,
                }}
                whileTap={{ scale: isSubmitting ? 1 : 0.95 }}
              >
                {isSubmitting ? (
                  <>
                    <div className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="size-4" />
                    <span>Send Message</span>
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </motion.button>
            </div>
          </motion.form>
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="mt-8 text-center text-2xl font-bold">
              Message Sent Successfully!
            </DialogTitle>

            <DialogDescription className="mt-4 text-center text-base">
              Thank you for reaching out! We&apos;re thrilled to connect with
              you and will get back to you as soon as possible.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={() => setIsDialogOpen(false)}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-teal-500 to-blue-500 px-6 py-3 text-sm font-semibold text-white transition-all hover:opacity-90"
            >
              <span>Awesome!</span>
              <ArrowRight className="size-4" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
